import { useCallback, useEffect, useMemo, useState } from "react";
import { CalendarDays, ChevronLeft, ChevronRight, Pencil, Plus } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { fmtFecha } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { useEmpresa } from "@/hooks/useEmpresa";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import PageHeader from "@/components/PageHeader";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";

function diasEnRangoISO(desde, hasta) {
  const out = [];
  const d = new Date(`${desde}T12:00:00`);
  const fin = new Date(`${hasta}T12:00:00`);
  while (d <= fin) {
    out.push(d.toISOString().slice(0, 10));
    d.setDate(d.getDate() + 1);
  }
  return out;
}

const INTENSIDAD = [
  "bg-white text-zinc-400 border-zinc-100",
  "bg-amber-100 text-amber-900 border-amber-200",
  "bg-amber-300 text-amber-950 border-amber-400",
  "bg-orange-400 text-white border-orange-500",
  "bg-orange-600 text-white border-orange-700",
  "bg-red-600 text-white border-red-700",
];

const DIAS = ["L", "M", "X", "J", "V", "S", "D"];

function estadoBadge(estado) {
  if (estado === "pendiente") return { cls: "border-amber-300 text-amber-700", label: "Pendiente aprobación" };
  if (estado === "pendiente_confirmacion") return { cls: "border-indigo-300 text-indigo-700", label: "Pend. confirmación" };
  return { cls: "border-green-300 text-green-700", label: "Aprobada" };
}

function MesMini({ mes, centroId, onDiaClick, onMesClick, diaSel, mesSel }) {
  const celdas = [];
  for (let i = 0; i < mes.primer_dia_semana; i++) {
    celdas.push(<div key={`e-${i}`} className="h-6" />);
  }
  mes.dias.forEach((d) => {
    const total = centroId ? (d.por_centro[String(centroId)] || 0) : d.total;
    let intensidad = 0;
    if (total > 0) {
      intensidad = total <= 1 ? 1 : total <= 2 ? 2 : total <= 4 ? 3 : total <= 6 ? 4 : 5;
    }
    const sel = diaSel === d.fecha;
    celdas.push(
      <button
        key={d.fecha}
        type="button"
        title={total > 0 ? `${total} en vacaciones` : ""}
        onClick={() => onDiaClick(d, mes)}
        className={`flex h-6 w-full items-center justify-center rounded border text-[10px] font-medium transition hover:ring-2 hover:ring-violet-300 ${INTENSIDAD[intensidad]} ${sel ? "ring-2 ring-violet-600" : ""}`}
      >
        {d.dia}
      </button>
    );
  });

  const badge =
    mes.personas_mes > 0
      ? `${mes.personas_mes} pers. · pico ${mes.pico_dia || 0}/día`
      : "0 pers.";

  return (
    <div className={`rounded-xl border bg-white p-3 shadow-sm ${mesSel === mes.mes ? "border-violet-400 ring-2 ring-violet-200" : "border-zinc-200"}`}>
      <div className="mb-2 flex items-center justify-between gap-1">
        <button
          type="button"
          onClick={() => onMesClick(mes)}
          className="text-left text-sm font-semibold text-zinc-800 hover:text-violet-700 hover:underline"
        >
          {mes.nombre}
        </button>
        <Badge variant="outline" className="shrink-0 text-[9px]" title="Personas distintas con vacaciones en el mes · máximo concurrentes en un día">
          {badge}
        </Badge>
      </div>
      <div className="mb-1 grid grid-cols-7 gap-0.5 text-center text-[9px] font-semibold text-zinc-400">
        {DIAS.map((x) => <span key={x}>{x}</span>)}
      </div>
      <div className="grid grid-cols-7 gap-0.5">{celdas}</div>
    </div>
  );
}

function ListaPeriodos({ items, onEditar, puedeEditar }) {
  if (!items?.length) {
    return <p className="mt-2 text-sm text-zinc-500">Nadie en vacaciones con los filtros actuales.</p>;
  }
  return (
    <ul className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
      {items.map((e) => {
        const b = estadoBadge(e.estado);
        const editable = !puedeEditar || puedeEditar(e);
        return (
          <li key={`${e.solicitud_id}-${e.usuario_id}`} className="rounded-lg bg-white px-3 py-2 text-sm shadow-sm">
            <div className="flex items-start justify-between gap-2">
              <p className="font-medium text-zinc-900">{e.nombre}</p>
              {onEditar && editable && (
                <button type="button" onClick={() => onEditar(e)} className="rounded p-1 text-zinc-400 hover:bg-violet-50 hover:text-violet-700" title="Modificar">
                  <Pencil className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
            <p className="text-xs text-zinc-500">{e.centro} · {e.departamento}</p>
            <p className="mt-1 text-xs font-medium text-violet-800">
              {fmtFecha(e.fecha_inicio)} — {fmtFecha(e.fecha_fin)}
            </p>
            <Badge variant="outline" className={`mt-1 text-[10px] ${b.cls}`}>{b.label}</Badge>
            {e.puede_ajustar === false && (
              <p className="mt-1 text-[10px] text-amber-700">Periodo pasado — solo admin puede corregir</p>
            )}
          </li>
        );
      })}
    </ul>
  );
}

export default function VacacionesCalendario() {
  const { user } = useAuth();
  const { empresa } = useEmpresa();
  const esAdmin = user?.rol === "admin";
  const [year, setYear] = useState(new Date().getFullYear());
  const [centroId, setCentroId] = useState("all");
  const [depId, setDepId] = useState("all");
  const [incluirPendientes, setIncluirPendientes] = useState(true);
  const [data, setData] = useState(null);
  const [diaSel, setDiaSel] = useState(null);
  const [mesSel, setMesSel] = useState(null);
  const [pendientes, setPendientes] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editando, setEditando] = useState(null);
  const [editAccion, setEditAccion] = useState("modificar_periodo");
  const [diasMantener, setDiasMantener] = useState([]);
  const [form, setForm] = useState({
    usuario_id: "",
    fecha_inicio: "",
    fecha_fin: "",
    motivo: "",
    comentario: "",
  });
  const [guardando, setGuardando] = useState(false);
  const [aprobando, setAprobando] = useState(null);

  const cargar = useCallback(async () => {
    try {
      const params = { year, incluir_pendientes: incluirPendientes };
      if (centroId !== "all") params.centro_id = Number(centroId);
      if (depId !== "all") params.departamento_id = Number(depId);
      const [cal, pend, us] = await Promise.all([
        api.get("/vacaciones/calendario", { params }),
        api.get("/vacaciones/pendientes"),
        api.get("/usuarios"),
      ]);
      setData(cal.data);
      setPendientes(pend.data || []);
      setUsuarios(us.data || []);
      setDiaSel(null);
      setMesSel(null);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [year, centroId, depId, incluirPendientes]);

  useEffect(() => { cargar(); }, [cargar]);

  const depsFiltrados = useMemo(() => {
    if (!data?.departamentos) return [];
    if (centroId === "all") return data.departamentos;
    return data.departamentos.filter((d) => d.centro_id === Number(centroId));
  }, [data, centroId]);

  const empleadosDia = useMemo(() => {
    if (!diaSel) return [];
    let list = diaSel.empleados || [];
    if (centroId !== "all") list = list.filter((e) => e.centro_id === Number(centroId));
    if (depId !== "all") list = list.filter((e) => e.departamento_id === Number(depId));
    const uniq = new Map();
    list.forEach((e) => uniq.set(e.solicitud_id, e));
    return [...uniq.values()];
  }, [diaSel, centroId, depId]);

  const empleadosMes = useMemo(() => {
    if (!mesSel) return [];
    let list = mesSel.periodos_mes || [];
    if (centroId !== "all") list = list.filter((e) => e.centro_id === Number(centroId));
    if (depId !== "all") list = list.filter((e) => e.departamento_id === Number(depId));
    return list;
  }, [mesSel, centroId, depId]);

  const onDiaClick = (d, mes) => {
    setDiaSel(d);
    setMesSel(null);
  };

  const onMesClick = (mes) => {
    setMesSel(mes);
    setDiaSel(null);
  };

  const abrirNueva = () => {
    setEditando(null);
    const hoy = new Date().toISOString().slice(0, 10);
    setForm({
      usuario_id: "",
      fecha_inicio: diaSel?.fecha || hoy,
      fecha_fin: diaSel?.fecha || hoy,
      motivo: "",
      comentario: "",
    });
    setDialogOpen(true);
  };

  const abrirEditar = (e) => {
    setEditando(e);
    setEditAccion("modificar_periodo");
    setDiasMantener(diasEnRangoISO(e.fecha_inicio, e.fecha_fin));
    setForm({
      usuario_id: String(e.usuario_id),
      fecha_inicio: e.fecha_inicio,
      fecha_fin: e.fecha_fin,
      motivo: e.motivo || "",
      comentario: "",
    });
    setDialogOpen(true);
  };

  const guardarVacacion = async () => {
    if (!editando && !form.usuario_id) {
      toast.error("Selecciona un empleado");
      return;
    }
    if (!form.motivo.trim() || form.motivo.trim().length < 3) {
      toast.error("Indica el motivo (mínimo 3 caracteres)");
      return;
    }
    setGuardando(true);
    try {
      if (editando) {
        if (editAccion === "cancelar") {
          await api.put(`/vacaciones/${editando.solicitud_id}`, {
            accion: "cancelar",
            comentario: form.comentario.trim(),
          });
          toast.success("Vacaciones canceladas.");
        } else if (editAccion === "modificar_dias") {
          if (!diasMantener.length) {
            toast.error("Selecciona al menos un día o cancela el periodo completo");
            setGuardando(false);
            return;
          }
          await api.put(`/vacaciones/${editando.solicitud_id}`, {
            accion: "modificar_dias",
            dias: diasMantener,
            motivo: form.motivo.trim(),
            comentario: form.comentario.trim(),
          });
          toast.success("Vacaciones ajustadas por días.");
        } else {
          await api.put(`/vacaciones/${editando.solicitud_id}`, {
            accion: "modificar_periodo",
            fecha_inicio: form.fecha_inicio,
            fecha_fin: form.fecha_fin,
            motivo: form.motivo.trim(),
            comentario: form.comentario.trim(),
          });
          toast.success("Vacaciones actualizadas. El trabajador deberá confirmar si ya estaban aprobadas.");
        }
      } else {
        await api.post("/vacaciones/asignar", {
          usuario_id: Number(form.usuario_id),
          fecha_inicio: form.fecha_inicio,
          fecha_fin: form.fecha_fin,
          motivo: form.motivo.trim(),
          comentario: form.comentario.trim(),
        });
        toast.success("Vacaciones asignadas. El trabajador recibirá aviso para confirmar.");
      }
      setDialogOpen(false);
      cargar();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setGuardando(false);
    }
  };

  const aprobarPendiente = async (p) => {
    setAprobando(p.id);
    try {
      await api.put(`/solicitudes/${p.id}/revisar`, { accion: "aprobar", comentario: "Aprobada desde calendario de vacaciones" });
      toast.success("Vacaciones aprobadas. El trabajador ha sido notificado.");
      cargar();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setAprobando(null);
    }
  };

  const usuariosFiltrados = useMemo(() => {
    let list = usuarios.filter((u) => {
      if (!u.activo) return false;
      if (empresa?.gestionar_gerencia) return ["worker", "manager"].includes(u.rol);
      return ["worker", "manager", "admin"].includes(u.rol);
    });
    if (depId !== "all") list = list.filter((u) => u.departamento_id === Number(depId));
    else if (centroId !== "all") {
      const depIds = new Set(depsFiltrados.map((d) => d.id));
      list = list.filter((u) => depIds.has(u.departamento_id));
    }
    return list;
  }, [usuarios, depId, centroId, depsFiltrados, empresa?.gestionar_gerencia]);

  return (
    <div className="space-y-6" data-testid="vacaciones-calendario-page">
      <PageHeader
        icon={CalendarDays}
        title="Calendario de vacaciones"
        subtitle="Vista anual de la carga de vacaciones por centro y departamento"
        sectionKey="vacaciones"
        brandGradient={false}
      >
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="ghost" size="icon" className="text-white hover:bg-white/20" onClick={() => setYear((y) => y - 1)}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="min-w-[4rem] text-center text-lg font-medium text-white">{year}</span>
          <Button variant="ghost" size="icon" className="text-white hover:bg-white/20" onClick={() => setYear((y) => y + 1)}>
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button onClick={abrirNueva} className="ml-2 bg-white/20 text-white hover:bg-white/30">
            <Plus className="mr-1.5 h-4 w-4" /> Definir vacaciones
          </Button>
        </div>
      </PageHeader>

      {pendientes.length > 0 && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50/80 p-4">
          <p className="mb-3 text-sm font-semibold text-amber-950">Solicitudes pendientes de aprobación</p>
          <div className="space-y-2">
            {pendientes.map((p) => (
              <div key={p.id} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-amber-100 bg-white px-4 py-3 text-sm">
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-medium text-zinc-900">{p.usuario}</p>
                    {p.tipo === "vacaciones_ajuste" && (
                      <Badge variant="outline" className="border-violet-300 text-violet-700 text-[10px]">Ajuste solicitado</Badge>
                    )}
                  </div>
                  <p className="text-xs text-zinc-500">{p.departamento || "—"}</p>
                  <p className="mt-0.5 text-xs font-medium text-violet-800">
                    {fmtFecha(p.fecha_inicio)} — {fmtFecha(p.fecha_fin)}
                  </p>
                  {p.motivo && <p className="mt-1 text-xs text-zinc-500">{p.motivo}</p>}
                </div>
                <Button
                  size="sm"
                  className="bg-emerald-600 hover:bg-emerald-700"
                  disabled={aprobando === p.id}
                  onClick={() => aprobarPendiente(p)}
                >
                  {aprobando === p.id ? "Aprobando…" : "Aprobar"}
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center justify-center gap-3 rounded-2xl border border-amber-100 bg-gradient-to-r from-amber-50/80 to-orange-50/80 p-4">
        {esAdmin && (
          <select
            className="h-9 min-w-[10rem] rounded-md border border-amber-100 bg-white px-2 text-sm"
            value={centroId}
            onChange={(e) => { setCentroId(e.target.value); setDepId("all"); }}
          >
            <option value="all">Todos los centros</option>
            {(data?.centros || []).map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
          </select>
        )}
        <select
          className="h-9 min-w-[10rem] rounded-md border border-amber-100 bg-white px-2 text-sm"
          value={depId}
          onChange={(e) => setDepId(e.target.value)}
        >
          <option value="all">Todos los departamentos</option>
          {depsFiltrados.map((d) => <option key={d.id} value={d.id}>{d.nombre}</option>)}
        </select>
        <label className="flex items-center gap-2 text-sm text-zinc-600">
          <input type="checkbox" checked={incluirPendientes} onChange={(e) => setIncluirPendientes(e.target.checked)} />
          Incluir pendientes
        </label>
      </div>

      <div className="flex flex-wrap items-center gap-3 text-xs text-zinc-500">
        <span>Carga:</span>
        {["Sin vacaciones", "Baja", "Media-baja", "Media", "Alta", "Muy alta"].map((t, i) => (
          <span key={t} className="flex items-center gap-1">
            <span className={`inline-block h-4 w-4 rounded border ${INTENSIDAD[i]}`} />
            {t}
          </span>
        ))}
        {data?.max_dia > 0 && <span className="ml-auto">Máx. concurrentes en un día: {data.max_dia} personas</span>}
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {(data?.meses || []).map((mes) => (
          <MesMini
            key={mes.mes}
            mes={mes}
            centroId={centroId !== "all" ? Number(centroId) : null}
            onDiaClick={onDiaClick}
            onMesClick={onMesClick}
            diaSel={diaSel?.fecha}
            mesSel={mesSel?.mes}
          />
        ))}
      </div>

      {diaSel && (
        <div className="rounded-xl border border-violet-200 bg-violet-50/50 p-4">
          <p className="font-semibold text-violet-900">
            {fmtFecha(diaSel.fecha)} — {empleadosDia.length} en vacaciones
          </p>
          <ListaPeriodos
            items={empleadosDia}
            onEditar={abrirEditar}
            puedeEditar={(e) => esAdmin || e.puede_ajustar !== false}
          />
        </div>
      )}

      {mesSel && !diaSel && (
        <div className="rounded-xl border border-orange-200 bg-orange-50/50 p-4">
          <p className="font-semibold text-orange-900">
            {mesSel.nombre} {year} — {empleadosMes.length} periodos de vacaciones
          </p>
          <ListaPeriodos
            items={empleadosMes}
            onEditar={abrirEditar}
            puedeEditar={(e) => esAdmin || e.puede_ajustar !== false}
          />
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editando ? "Modificar vacaciones" : "Definir vacaciones"}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-zinc-500">
            {editando
              ? editando.puede_ajustar === false
                ? "Periodo pasado: la corrección quedará pendiente de confirmación del trabajador."
                : "Al cambiar fechas de vacaciones ya aprobadas, el trabajador deberá confirmar de nuevo."
              : "El trabajador recibirá notificación para confirmar o solicitar cambios."}
          </p>
          <div className="space-y-3">
            {!editando && (
              <div className="space-y-1.5">
                <Label>Empleado</Label>
                <Select value={form.usuario_id} onValueChange={(v) => setForm({ ...form, usuario_id: v })}>
                  <SelectTrigger><SelectValue placeholder="Selecciona empleado" /></SelectTrigger>
                  <SelectContent>
                    {usuariosFiltrados.map((u) => (
                      <SelectItem key={u.id} value={String(u.id)}>{u.nombre} {u.apellidos}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {editando && (
              <p className="rounded-lg bg-violet-50 px-3 py-2 text-sm font-medium text-violet-900">{editando.nombre}</p>
            )}
            {editando && (
              <div className="space-y-1.5">
                <Label>Tipo de ajuste</Label>
                <select
                  className="h-9 w-full rounded-md border border-zinc-200 px-2 text-sm"
                  value={editAccion}
                  onChange={(e) => setEditAccion(e.target.value)}
                >
                  <option value="modificar_periodo">Cambiar periodo completo</option>
                  <option value="modificar_dias">Mantener solo algunos días</option>
                  <option value="cancelar">Cancelar vacaciones</option>
                </select>
              </div>
            )}
            {editando && editAccion === "modificar_dias" && (
              <div className="space-y-2 rounded-lg border border-violet-100 bg-violet-50/40 p-3">
                <p className="text-xs font-medium text-violet-900">Marca los días que se mantienen</p>
                <div className="grid max-h-40 grid-cols-2 gap-2 overflow-y-auto">
                  {diasEnRangoISO(editando.fecha_inicio, editando.fecha_fin).map((dia) => (
                    <label key={dia} className="flex items-center gap-2 text-xs text-zinc-700">
                      <Checkbox
                        checked={diasMantener.includes(dia)}
                        onCheckedChange={(v) => {
                          setDiasMantener((prev) => (
                            v ? [...prev, dia].sort() : prev.filter((x) => x !== dia)
                          ));
                        }}
                      />
                      {fmtFecha(dia)}
                    </label>
                  ))}
                </div>
              </div>
            )}
            {(!editando || editAccion === "modificar_periodo") && (
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1.5">
                  <Label>Desde</Label>
                  <Input type="date" value={form.fecha_inicio} onChange={(e) => setForm({ ...form, fecha_inicio: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label>Hasta</Label>
                  <Input type="date" value={form.fecha_fin} onChange={(e) => setForm({ ...form, fecha_fin: e.target.value })} />
                </div>
              </div>
            )}
            {editAccion !== "cancelar" && (
              <div className="space-y-1.5">
                <Label>Motivo</Label>
                <Textarea value={form.motivo} onChange={(e) => setForm({ ...form, motivo: e.target.value })} />
              </div>
            )}
            <div className="space-y-1.5">
              <Label>Comentario (opcional)</Label>
              <Textarea value={form.comentario} onChange={(e) => setForm({ ...form, comentario: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={guardarVacacion} disabled={guardando} className={editAccion === "cancelar" ? "bg-red-600 hover:bg-red-700" : "bg-violet-600 hover:bg-violet-700"}>
              {guardando ? "Guardando…" : editando
                ? (editAccion === "cancelar" ? "Confirmar cancelación" : "Guardar cambios")
                : "Asignar y notificar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
